/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  ButtonBase,
  Collapse,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';

import DataTableSkeleton from '../data-table-skeleton/data-table-skeleton';
import Iconify from '../iconify';

interface DataRowProps {
  row: any;
  disableSwitch?: boolean;
  disableEdit?: boolean;
  disableDelete?: boolean;
  checked?: string[];
  collapseNewIdCondition: string;
  collapseConfig: (row: any) => JSX.Element;
  rowConfig: (row: any) => JSX.Element;
  handleDelete: (row: any) => void;
  setChecked?: (id: string) => void;
}

function DataRow({
  row,
  disableSwitch = false,
  disableEdit = false,
  disableDelete = false,
  checked,
  collapseNewIdCondition = '-',
  collapseConfig,
  rowConfig,
  handleDelete,
  setChecked,
}: DataRowProps) {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<string | undefined>();

  useEffect(() => {
    if (row.id === collapseNewIdCondition) {
      setCollapsed(row.id);
    }
  }, [row]);

  const handleCollapse = (id: string) => {
    if (id === collapsed) {
      setCollapsed(undefined);
    } else {
      setCollapsed(id);
    }
  };

  console.log('re-render-row');

  return (
    <>
      <TableRow sx={{ borderBottom: `1px solid${theme.palette.greyScale[100]}` }}>
        {!disableSwitch && (
          <TableCell align="center" sx={{ px: 2 }} padding="checkbox">
            <Box marginY={3} typography="body-md">
              <Switch
                size="medium"
                disableRipple
                disableTouchRipple
                checked={checked?.includes(row.id)}
                onChange={() => (setChecked ? setChecked(row.id) : () => {})}
              />
            </Box>
          </TableCell>
        )}
        {rowConfig(row)}
        {disableEdit ? (
          <TableCell align="center" padding="checkbox" sx={{ px: 4 }}>
            <Box marginY={3}>
              <Iconify
                onClick={() => handleCollapse(row.id)}
                sx={{
                  cursor: 'pointer',
                  transition: '0.3s',
                  ':hover': { color: `${theme.palette.feedback.info}ad`, transition: '0.3s' },
                }}
                icon={collapsed === row.id ? 'ri:arrow-up-s-line' : 'ri:arrow-down-s-line'}
                color={theme.palette.feedback.info}
              />
            </Box>
          </TableCell>
        ) : (
          <TableCell align="center" padding="checkbox" sx={{ px: 4 }}>
            <Box marginY={3}>
              <Iconify
                onClick={() => handleCollapse(row.id)}
                sx={{
                  cursor: 'pointer',
                  transition: '0.3s',
                  ':hover': { color: `${theme.palette.feedback.info}ad`, transition: '0.3s' },
                }}
                icon={collapsed === row.id ? 'ri:arrow-up-s-line' : 'ri:pencil-line'}
                color={theme.palette.feedback.info}
              />
            </Box>
          </TableCell>
        )}
        {!disableDelete && (
          <TableCell align="center" padding="checkbox" sx={{ px: 4 }}>
            <Box marginY={3}>
              <Iconify
                onClick={() => handleDelete(row)}
                sx={{
                  cursor: 'pointer',
                  transition: '0.3s',
                  ':hover': { color: `${theme.palette.feedback.danger}ad`, transition: '0.3s' },
                }}
                icon="ri:delete-bin-6-line"
                color={theme.palette.feedback.danger}
              />
            </Box>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={40}>
          <Collapse in={collapsed === row.id}>
            <Box bgcolor={`${theme.palette.greyScale[50]}33`} width="100%" p={4}>
              {collapsed === row.id && collapseConfig(row)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const MemoizedDataRow = memo(DataRow);

export interface DataTableProps {
  isLoading: boolean;
  rows: any[];
  header: { label: string; id: string; sortable?: boolean; open?: boolean }[];
  orderBy: string;
  order: 'asc' | 'desc';
  disableSwitch?: boolean;
  disableEdit?: boolean;
  disableDelete?: boolean;
  checked?: string[];
  rowsPerPage: number;
  count: number;
  page: number;
  collapseNewIdCondition: string;
  setChecked?: (id: string) => void;
  rowConfig: (row: any) => JSX.Element;
  createSortHandler: (id: string) => void;
  collapseConfig: (row: any) => JSX.Element;
  handleDelete: (row: any) => void;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
}

function DataTable({
  isLoading,
  rows,
  header,
  order,
  orderBy,
  rowsPerPage,
  count,
  page,
  checked,
  disableDelete,
  disableEdit,
  disableSwitch,
  collapseNewIdCondition,
  setChecked,
  rowConfig,
  createSortHandler,
  collapseConfig,
  handleDelete,
  onPageChange,
  onRowsPerPageChange,
}: DataTableProps) {
  return (
    <div data-testid="dataTable-render-test">
      <TableContainer>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              {header.map((headCell, index) => (
                <TableCell
                  key={headCell.id}
                  align="center"
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{ borderRight: index !== header.length - 1 ? '1px solid black !important' : 'none', bgcolor: 'transparent' }}
                >
                  <ButtonBase
                    sx={{
                      width: '100%',
                      ':hover': {
                        cursor: headCell.sortable ? 'pointer' : 'default',
                      },
                    }}
                    disableRipple
                    onClick={headCell.sortable ? () => createSortHandler(headCell.id) : () => {}}
                  >
                    <Box width={20} />
                    <Typography typography="bold-bd-lg" color="greyScale.600">
                      {headCell.label}
                    </Typography>
                    {orderBy === headCell.id && headCell.sortable ? (
                      <Iconify
                        height={20}
                        width={20}
                        color="greyScale.200"
                        icon={order === 'asc' ? 'material-symbols:arrow-warm-up' : 'material-symbols:arrow-cool-down'}
                      />
                    ) : (
                      <Box width={20} />
                    )}
                  </ButtonBase>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <Box height={8} />
          <TableBody>
            {isLoading ? (
              <DataTableSkeleton length={header.length} />
            ) : (
              rows.map((row, index) => (
                <MemoizedDataRow
                  key={index}
                  collapseConfig={collapseConfig}
                  handleDelete={handleDelete}
                  row={row}
                  rowConfig={rowConfig}
                  disableDelete={disableDelete}
                  disableEdit={disableEdit}
                  disableSwitch={disableSwitch}
                  checked={checked}
                  setChecked={setChecked}
                  collapseNewIdCondition={collapseNewIdCondition}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage="Linhas por página"
        rowsPerPageOptions={[10, 15, 20, 30, 50]}
      />
    </div>
  );
}

export default memo(DataTable);
